import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "",
  timeout: 120000,
});

let isRefreshing = false;
let pendingQueue = [];

const flushQueue = (error, token = null) => {
  pendingQueue.forEach((cb) => (error ? cb.reject(error) : cb.resolve(token)));
  pendingQueue = [];
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) throw new Error("no refresh token");
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL || ""}/api/auth/refresh`,
    { refresh_token: refreshToken },
    { timeout: 15000 }
  );
  const newToken = res?.data?.token;
  if (!newToken) throw new Error("refresh response missing token");
  localStorage.setItem("token", newToken);
  return newToken;
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
  if (typeof window !== "undefined") {
    const onAuthPage = /\/login|\/register|\/oauth\//.test(window.location.pathname);
    if (!onAuthPage) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?next=${next}`;
    }
  }
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config || {};
    const status = err.response?.status;

    if (status === 401 && !originalRequest._retry) {
      const url = originalRequest.url || "";
      if (url.includes("/api/auth/refresh") || url.includes("/api/auth/login")) {
        clearAuthAndRedirect();
        return Promise.reject(err);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        flushQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshErr) {
        flushQueue(refreshErr, null);
        clearAuthAndRedirect();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

export default api;
